import { useEffect, useState } from "react";
import { Save, Share2, Mail, Trash2, Inbox, Twitch } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const NETWORKS = [
  { key: "twitch", label: "Twitch", placeholder: "https://twitch.tv/emisphere" },
  { key: "twitter", label: "X / Twitter", placeholder: "https://x.com/emisphere" },
  { key: "instagram", label: "Instagram", placeholder: "https://instagram.com/emisphere" },
  { key: "youtube", label: "YouTube", placeholder: "https://youtube.com/@emisphere" },
  { key: "discord", label: "Discord", placeholder: "https://discord.gg/emisphere" },
];

const inputCls = "w-full rounded-lg border border-input bg-[#060B18] px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500";

export default function SocialsTab() {
  const [links, setLinks] = useState({ twitch: "", twitter: "", instagram: "", youtube: "", discord: "" });
  const [contact, setContact] = useState({ email: "", discord: "", intro: "" });
  const [streamers, setStreamers] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadMessages = () => api.get("/admin/contacts").then((r) => setMessages(r.data)).catch(() => {});

  useEffect(() => {
    Promise.all([api.get("/socials"), api.get("/contact-info")])
      .then(([s, c]) => { setLinks((prev) => ({ ...prev, ...s.data })); setContact(c.data); })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
    api.get("/streamers").then((r) => setStreamers(r.data.channels.join("\n"))).catch(() => {});
    loadMessages();
  }, []);

  const saveStreamers = async (e) => {
    e.preventDefault();
    try {
      const r = await api.put("/admin/streamers", { channels: streamers.split("\n") });
      setStreamers(r.data.channels.join("\n"));
      toast.success(`${r.data.channels.length} streamer(s) enregistré(s)`);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await Promise.all([
        api.put("/admin/socials", links),
        api.put("/admin/contact-info", contact),
      ]);
      toast.success("Réseaux & contact mis à jour");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const removeMessage = async (id) => {
    try {
      await api.delete(`/admin/contacts/${id}`);
      toast.success("Message supprimé");
      loadMessages();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (loading) return <p className="text-slate-500">Chargement...</p>;

  return (
    <div className="grid max-w-2xl gap-8">
      <form onSubmit={save} className="card-tactical grid gap-4 p-6" data-testid="admin-socials-form">
        <p className="flex items-center gap-2 font-heading text-lg font-bold uppercase">
          <Share2 className="h-5 w-5 text-orange-500" /> Liens réseaux sociaux (pied de page & contact)
        </p>
        <p className="text-xs text-slate-500">
          Les icônes correspondantes apparaissent en bas de chaque page et sur la page Contact. Laissez vide pour masquer un réseau.
        </p>
        {NETWORKS.map((n) => (
          <label key={n.key} className="text-xs font-bold uppercase tracking-widest text-slate-400">
            {n.label}
            <input value={links[n.key]} placeholder={n.placeholder} data-testid={`admin-social-${n.key}`}
              onChange={(e) => setLinks({ ...links, [n.key]: e.target.value })} className={inputCls + " mt-1"} />
          </label>
        ))}

        <p className="mt-4 flex items-center gap-2 border-t border-orange-500/10 pt-4 font-heading text-lg font-bold uppercase">
          <Mail className="h-5 w-5 text-orange-500" /> Page Contact
        </p>
        <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
          Email de contact (affiché + reçoit les messages du formulaire)
          <input type="email" value={contact.email} placeholder="contact@emisphere.gg" data-testid="admin-contact-email"
            onChange={(e) => setContact({ ...contact, email: e.target.value })} className={inputCls + " mt-1"} />
        </label>
        <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
          Lien d'invitation Discord
          <input value={contact.discord} placeholder="https://discord.gg/emisphere" data-testid="admin-contact-discord"
            onChange={(e) => setContact({ ...contact, discord: e.target.value })} className={inputCls + " mt-1"} />
        </label>
        <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
          Texte d'introduction de la page Contact
          <textarea value={contact.intro} rows={2} data-testid="admin-contact-intro"
            onChange={(e) => setContact({ ...contact, intro: e.target.value })} className={inputCls + " mt-1"} />
        </label>
        <button type="submit" data-testid="admin-socials-save-button"
          className="flex w-fit items-center gap-2 rounded-lg bg-orange-500 px-5 py-2.5 text-sm font-bold uppercase text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400">
          <Save className="h-4 w-4" /> Enregistrer
        </button>
      </form>

      <form onSubmit={saveStreamers} className="card-tactical grid gap-4 p-6" data-testid="admin-streamers-form">
        <p className="flex items-center gap-2 font-heading text-lg font-bold uppercase">
          <Twitch className="h-5 w-5 text-orange-500" /> Streamers (Match Center)
        </p>
        <p className="text-xs text-slate-500">
          Une chaîne Twitch par ligne (pseudo ou lien twitch.tv). Le premier est affiché par défaut. Laissez vide pour masquer la section.
        </p>
        <textarea value={streamers} rows={5} placeholder={"emisphere\nhttps://twitch.tv/mon_joueur"} data-testid="admin-streamers-input"
          onChange={(e) => setStreamers(e.target.value)} className={inputCls} />
        <button type="submit" data-testid="admin-streamers-save-button"
          className="flex w-fit items-center gap-2 rounded-lg bg-orange-500 px-5 py-2.5 text-sm font-bold uppercase text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400">
          <Save className="h-4 w-4" /> Enregistrer
        </button>
      </form>

      <div className="card-tactical grid gap-4 p-6" data-testid="admin-messages-section">
        <p className="flex items-center gap-2 font-heading text-lg font-bold uppercase">
          <Inbox className="h-5 w-5 text-orange-500" /> Messages reçus ({messages.length})
        </p>
        {messages.length === 0 && <p className="text-sm text-slate-500">Aucun message pour le moment.</p>}
        {messages.map((m) => (
          <div key={m.id} data-testid={`admin-message-${m.id}`} className="flex items-start justify-between gap-3 rounded-lg border border-orange-500/10 bg-[#060B18] p-4">
            <div>
              <p className="text-sm font-bold text-white">{m.name} <span className="font-normal text-slate-400">— {m.email}</span></p>
              {m.subject && <p className="text-xs font-bold uppercase tracking-widest text-orange-400">{m.subject}</p>}
              <p className="mt-1 text-sm text-slate-300">{m.message}</p>
              <p className="mt-1 text-[11px] text-slate-500">{new Date(m.created_at).toLocaleString("fr-FR")}</p>
            </div>
            <button onClick={() => removeMessage(m.id)} data-testid={`admin-message-delete-${m.id}`} aria-label="Supprimer le message"
              className="rounded-lg border border-red-500/40 p-2 text-red-400 transition-all hover:bg-red-500/10">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

